import { Suspense, lazy } from "react";
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import { ThemeProvider } from "next-themes";

// Eagerly load Auth - it's the landing/LCP page
import Auth from "./pages/Auth";

// ProtectedRoute lazy-loaded to prevent module-init call of useAuth before AuthProvider mounts
const ProtectedRoute = lazy(() =>
  import("@/components/layout/ProtectedRoute").then((m) => ({ default: m.ProtectedRoute }))
);

// Eagerly load the landing page to avoid critical request chain
import Index from "./pages/Index";
const NotFound = lazy(() => import("./pages/NotFound"));
const CashierHome = lazy(() => import("./pages/CashierHome"));

// Lazy load non-critical pages to reduce initial CSS/JS bundle
const Setup = lazy(() => import("./pages/Setup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/Products"));
const POS = lazy(() => import("./pages/POS"));
const Customers = lazy(() => import("./pages/Customers"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Users = lazy(() => import("./pages/Users"));
const Staff = lazy(() => import("./pages/Staff"));
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
const Financial = lazy(() => import("./pages/Financial"));
const BranchManagement = lazy(() => import("./pages/BranchManagement"));
const SecurityCompliance = lazy(() => import("./pages/SecurityCompliance"));
const CustomerEngagement = lazy(() => import("./pages/CustomerEngagement"));
const OwnerView = lazy(() => import("./pages/OwnerView"));
const Kitchen = lazy(() => import("./pages/Kitchen"));
const TabletOrder = lazy(() => import("./pages/TabletOrder"));
const EndOfDayReport = lazy(() => import("./pages/EndOfDayReport"));
const TableManagement = lazy(() => import("./pages/TableManagement"));
const Procurement = lazy(() => import("./pages/Procurement"));
const Delivery = lazy(() => import("./pages/Delivery"));
const DigitalMenuBoard = lazy(() => import("./pages/DigitalMenuBoard"));
const CustomerMenu = lazy(() => import("./pages/CustomerMenu"));
const OrderApproval = lazy(() => import("./pages/OrderApproval"));

const WhatsAppConnect = lazy(() => import("./pages/WhatsAppConnect"));
const OrderReceiver = lazy(() => import("./pages/OrderReceiver"));
const RiderPortal = lazy(() => import("./pages/RiderPortal"));

// Branch Manager portal - lazy loaded
const BranchManagerLayout = lazy(() => import("./pages/branch-manager/BranchManagerLayout"));
const BranchManagerDashboard = lazy(() => import("./pages/branch-manager/BranchManagerDashboard"));
const BranchManagerStaff = lazy(() => import("./pages/branch-manager/BranchManagerStaff"));
const BranchManagerReports = lazy(() => import("./pages/branch-manager/BranchManagerReports"));
const BranchManagerPOS = lazy(() => import("./pages/branch-manager/BranchManagerPOS"));
const BranchManagerKitchen = lazy(() => import("./pages/branch-manager/BranchManagerKitchen"));
const BranchManagerTables = lazy(() => import("./pages/branch-manager/BranchManagerTables"));
const BranchManagerAttendance = lazy(() => import("./pages/branch-manager/BranchManagerAttendance"));
const BranchManagerEOD = lazy(() => import("./pages/branch-manager/BranchManagerEOD"));
const BranchManagerDelivery = lazy(() => import("./pages/branch-manager/BranchManagerDelivery"));
const BranchManagerProcurement = lazy(() => import("./pages/branch-manager/BranchManagerProcurement"));
const BranchManagerShifts = lazy(() => import("./pages/branch-manager/BranchManagerShifts"));

// Super Admin pages - lazy loaded
const SuperAdminAuth = lazy(() => import("./pages/superadmin/SuperAdminAuth"));
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/SuperAdminDashboard"));
const Companies = lazy(() => import("./pages/superadmin/Companies"));
const Branches = lazy(() => import("./pages/superadmin/Branches"));
const Terminals = lazy(() => import("./pages/superadmin/Terminals"));
const RecycleBin = lazy(() => import("./pages/superadmin/RecycleBin"));
const SuperAdminUsers = lazy(() => import("./pages/superadmin/Users"));
const Subscriptions = lazy(() => import("./pages/superadmin/Subscriptions"));
const Billing = lazy(() => import("./pages/superadmin/Billing"));
const Themes = lazy(() => import("./pages/superadmin/Themes"));
const ExportPermissions = lazy(() => import("./pages/superadmin/ExportPermissions"));
const SuperAdminSettings = lazy(() => import("./pages/superadmin/Settings"));
const CompanyDetail = lazy(() => import("./pages/superadmin/CompanyDetail"));

const queryClient = new QueryClient();

// Error boundary to catch async chunk load failures
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', background: '#09090b', color: '#fff', fontFamily: 'system-ui' }}>
          <p style={{ fontSize: '18px' }}>Failed to load. Please refresh the page.</p>
          <button onClick={() => { window.location.reload(); }} style={{ padding: '8px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Minimal loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <LanguageProvider>
      <AuthProvider>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/cashier-home" element={<ProtectedRoute><CashierHome /></ProtectedRoute>} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
              <Route path="/pos" element={<ProtectedRoute><POS /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
              <Route path="/staff" element={<ProtectedRoute><Staff /></ProtectedRoute>} />
              <Route path="/activity-log" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
              <Route path="/financial" element={<ProtectedRoute><Financial /></ProtectedRoute>} />
              <Route path="/branches" element={<ProtectedRoute><BranchManagement /></ProtectedRoute>} />
              <Route path="/security" element={<ProtectedRoute><SecurityCompliance /></ProtectedRoute>} />
              <Route path="/engagement" element={<ProtectedRoute><CustomerEngagement /></ProtectedRoute>} />
              <Route path="/owner" element={<ProtectedRoute><OwnerView /></ProtectedRoute>} />
              <Route path="/kitchen" element={<ProtectedRoute><Kitchen /></ProtectedRoute>} />
              <Route path="/tablet-order" element={<ProtectedRoute><TabletOrder /></ProtectedRoute>} />
              <Route path="/eod-report" element={<ProtectedRoute><EndOfDayReport /></ProtectedRoute>} />
              <Route path="/tables" element={<ProtectedRoute><TableManagement /></ProtectedRoute>} />
              <Route path="/procurement" element={<ProtectedRoute><Procurement /></ProtectedRoute>} />
              <Route path="/delivery" element={<ProtectedRoute><Delivery /></ProtectedRoute>} />
              <Route path="/menu-board" element={<ProtectedRoute><DigitalMenuBoard /></ProtectedRoute>} />
              <Route path="/menu/:businessId" element={<CustomerMenu />} />
              <Route path="/order-approval" element={<ProtectedRoute><OrderApproval /></ProtectedRoute>} />
              <Route path="/whatsapp-connect" element={<ProtectedRoute><WhatsAppConnect /></ProtectedRoute>} />
              <Route path="/order-receiver" element={<ProtectedRoute><OrderReceiver /></ProtectedRoute>} />
              <Route path="/rider" element={<RiderPortal />} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/settings/*" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              {/* Branch Manager Portal */}
              <Route path="/branch-manager" element={<ProtectedRoute><BranchManagerLayout /></ProtectedRoute>}>
                <Route index element={<BranchManagerDashboard />} />
                <Route path="staff" element={<BranchManagerStaff />} />
                <Route path="reports" element={<BranchManagerReports />} />
                <Route path="pos" element={<BranchManagerPOS />} />
                <Route path="kitchen" element={<BranchManagerKitchen />} />
                <Route path="tables" element={<BranchManagerTables />} />
                <Route path="attendance" element={<BranchManagerAttendance />} />
                <Route path="eod-report" element={<BranchManagerEOD />} />
                <Route path="delivery" element={<BranchManagerDelivery />} />
                <Route path="procurement" element={<BranchManagerProcurement />} />
                <Route path="shifts" element={<BranchManagerShifts />} />
              </Route>
              {/* Super Admin Routes */}
              <Route path="/superadmin" element={<SuperAdminAuth />} />
              <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/superadmin/companies" element={<Companies />} />
              <Route path="/superadmin/companies/new" element={<Companies />} />
              <Route path="/superadmin/companies/:id" element={<CompanyDetail />} />
              <Route path="/superadmin/branches" element={<Branches />} />
              <Route path="/superadmin/terminals" element={<Terminals />} />
              <Route path="/superadmin/settings" element={<SuperAdminSettings />} />
              <Route path="/superadmin/recycle-bin" element={<RecycleBin />} />
              <Route path="/superadmin/users" element={<SuperAdminUsers />} />
              <Route path="/superadmin/subscriptions" element={<Subscriptions />} />
              <Route path="/superadmin/billing" element={<Billing />} />
              <Route path="/superadmin/themes" element={<Themes />} />
              <Route path="/superadmin/export-permissions" element={<ExportPermissions />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
